import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { backendEndPointToken } from '../app.config';
import { Observation } from 'fhir/r4';
import { ErrorService } from './error.service';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { StateService } from './state.service';
import { referenceArrayContains } from './fhir-reference.util';
import { UtilityService } from './utility.service';

@Injectable({ providedIn: 'root' })
export class ObservationFinalizationService {
  private readonly http = inject(HttpClient);
  private readonly backendEndPoint = inject(backendEndPointToken);
  private readonly dialog = inject(MatDialog);
  private readonly errorService = inject(ErrorService);
  private readonly stateService = inject(StateService);
  private readonly utilityService = inject(UtilityService);

  /**
   * Validates a single Observation that is about to be saved as FINAL.
   * - If there are existing FINAL observations under the same ServiceRequest with the same code,
   *   prompts the user to cancel the previous finals.
   * - If user confirms, cancels them (PATCH status=cancelled).
   * Returns true if it is safe to proceed with saving the observation.
   */
  validateSingleFinalization$(opts: {
    serviceRequestRef: string | null | undefined;
    observationToSave: Observation & any;
  }): Observable<boolean> {
    const srRef = (opts?.serviceRequestRef || '').toString().trim();
    const observation = (opts?.observationToSave || {}) as any;

    const desiredStatus = this.normalize(observation?.status);
    if (desiredStatus !== 'final') return of(true);

    if (!srRef) return of(true);
    if (!observation?.code) return of(true);

    return this.listObservationsForServiceRequest(srRef).pipe(
      map((list: Observation[]) => {
        const currentId = (observation?.id || '').toString();
        return (list || []).filter((o: any) => {
          const oid = (o?.id || '').toString();
          if (!oid || (currentId && oid === currentId)) return false;
          if (this.normalize(o?.status) !== 'final') return false;
          return this.codesMatch(o?.code, observation?.code);
        }) as Observation[];
      }),
      switchMap((conflicts: Observation[]) => {
        if (!conflicts.length) return of(true);
        return this.confirmCancelPreviousFinalObservations(conflicts).pipe(
          switchMap((confirmed: boolean) => {
            if (!confirmed) return of(false);
            return this.cancelObservations$(conflicts);
          })
        );
      })
    );
  }

  /**
   * Validates a batch of Observations that are about to be saved as FINAL.
   * - Validates conflicts among the batch (prevents multiple FINAL results for the same code).
   * - Validates conflicts against existing server/state observations for the same ServiceRequest.
   * - Optionally cancels previous finals if user confirms.
   */
  validateBatchFinalization$(opts: {
    serviceRequestRef: string | null | undefined;
    observationsToSave: Array<(Observation & any) | null | undefined>;
  }): Observable<boolean> {
    const srRef = (opts?.serviceRequestRef || '').toString().trim();
    const batch = (opts?.observationsToSave || []).filter(Boolean) as Array<Observation & any>;

    const finalOnes = batch.filter((o) => this.normalize((o as any)?.status) === 'final');
    const unCancelledObs = batch.filter((o) => this.normalize((o as any)?.status) !== 'cancelled');
    // if (!finalOnes.length) return of(true);

    // const seenCodes = new Set<string>();

    // for (const obs of finalOnes) {
    //   const code = obs?.code;

    //   if (!code) continue;

    //   if (seenCodes.has(code)) {
    //     this.errorService.openandCloseError(
    //       'Multiple FINAL results for the same lab test were found in this submission. Please submit one FINAL result per test.'
    //     );
    //     return of(false);
    //   }

    //   seenCodes.add(code);
    // }

    // // return of(true);


    // In-batch validation: prevent multiple FINAL observations for the same code.
    // (A ServiceRequest can produce multiple observations, but not multiple FINALs for the same test code.)
    for (let i = 0; i < unCancelledObs.length; i++) {
      for (let j = i + 1; j < unCancelledObs.length; j++) {
        if (this.codesMatch(unCancelledObs[i]?.code, unCancelledObs[j]?.code)) {
          this.errorService.openandCloseError(
            'Multiple results for the same service are headed for confirmation in this submission. Please make sure that all but one of the duplicated is cancelled. Alternatively, you can confirm per row'
          );
          return of(false);
        }
      }
    }

    // If we can't anchor to a ServiceRequest, we can only do in-batch checks.
    if (!srRef) return of(true);

    return this.listObservationsForServiceRequest(srRef).pipe(
      map((list: Observation[]) => {
        const batchIds = new Set(finalOnes.map((o: any) => (o?.id || '').toString()).filter(Boolean));
        const finalCodes = finalOnes.map((o: any) => o?.code).filter(Boolean);

        return (list || []).filter((o: any) => {
          const oid = (o?.id || '').toString();
          if (!oid) return false;
          if (batchIds.has(oid)) return false; // don't conflict with itself
          if (this.normalize(o?.status) !== 'final') return false;
          return finalCodes.some((c) => this.codesMatch(o?.code, c));
        }) as Observation[];
      }),
      switchMap((conflicts: Observation[]) => {
        if (!conflicts.length) return of(true);
        return this.confirmCancelPreviousFinalObservations(conflicts).pipe(
          switchMap((confirmed: boolean) => {
            if (!confirmed) return of(false);
            return this.cancelObservations$(conflicts);
          })
        );
      })
    );
  }

  private cancelObservations$(observations: Observation[]): Observable<boolean> {
    const patches = (observations || [])
      .filter((o: any) => !!o?.id)
      .map((o: any) =>
        this.http
          .patch<any>(
            `${this.backendEndPoint}/Observation/${o.id}`,
            [{ op: 'replace', path: '/status', value: 'cancelled' }],
            {
              headers: {
                'Content-Type': 'application/fhir+json',
                Accept: 'application/fhir+json',
                Prefer: 'return=representation'
              }
            }
          )
          .pipe(
            map((resp: any) => {
              // Server may return OperationOutcome; still update local state best-effort.
              if (resp && resp.resourceType && resp.resourceType !== 'OperationOutcome') return resp as Observation;
              return { ...(o as any), status: 'cancelled' } as Observation;
            }),
            tap((updated: Observation) => {
              this.stateService.persistLocalResource(updated as any, 'saved');
            }),
            map(() => true)
          )
      );

    if (!patches.length) return of(true);

    return forkJoin(patches).pipe(
      map(() => true),
      catchError((err) => {
        console.error('Failed to cancel previous final observations', err);
        this.errorService.openandCloseError('Unable to cancel the previous final result(s).');
        return of(false);
      })
    );
  }

  private confirmCancelPreviousFinalObservations(conflicts: Observation[]): Observable<boolean> {
    const count = (conflicts || []).length;
    const msg =
      count === 1
        ? 'There is already a FINAL result for this same lab test under this request.\n\nDo you want to cancel the previous final result and save this one as FINAL?'
        : `There are already ${count} FINAL results for this same lab test under this request.\n\nDo you want to cancel the previous final results and save this one as FINAL?`;

    const ref = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '520px',
      data: {
        title: 'Replace final result?',
        message: msg,
        confirmText: 'Yes, replace',
        cancelText: 'No, keep existing',
        icon: 'warning'
      }
    });

    return ref.afterClosed().pipe(map((v: any) => v === true));
  }

  private listObservationsForServiceRequest(srRef: string): Observable<Observation[]> {
    const patientRef = this.stateService.getPatientReference()?.reference;
    const patientId = patientRef ? patientRef.split('/')[1] : null;

    // Use the shared three-tier strategy (one-shot) and still apply a robust basedOn filter.
    // `UtilityService.valueMatches` supports absolute-vs-relative reference matching.
    return this.utilityService.threeTierQueryAndSyncToStateOnce$<Observation>({
      resourceType: 'Observation',
      // patientId: patientId ?? null,
      useCurrentPatientId: false,
      urlSearchObject: {
        'based-on': srRef,
        ...(patientId ? { subject: `Patient/${patientId}` } : {})
      },
      querySearchObject: [
        { key: 'basedOn.reference', value: srRef },
        ...(patientId ? [{ key: 'subject.reference', value: `Patient/${patientId}` }] : [])
      ]
    }).pipe(
      map((list: Observation[]) => (list || []).filter((o: any) => referenceArrayContains(o?.basedOn, srRef)))
    );
  }

  private normalize(v: any): string {
    return (v == null ? '' : String(v)).trim().toLowerCase();
  }

  private codesMatch(a: any, b: any): boolean {
    if (!a || !b) return false;

    const aCodes = this.extractCodes(a);
    const bCodes = this.extractCodes(b);

    // If both have codes, treat as duplicate if ANY coding.code matches (ignore system).
    if (aCodes.size && bCodes.size) {
      for (const code of aCodes) {
        if (bCodes.has(code)) return true;
      }
      return false;
    }

    // If only one has codes, don't match against text.
    if (aCodes.size || bCodes.size) return false;

    // Fall back to CodeableConcept.text only when neither side has coding.code.
    const aText = this.extractText(a);
    const bText = this.extractText(b);
    return !!aText && aText === bText;
  }

  private extractCodes(cc: any): Set<string> {
    const out = new Set<string>();
    const codings = (cc as any)?.coding;
    if (!Array.isArray(codings)) return out;
    for (const c of codings) {
      const code = (c?.code || '').toString().trim();
      if (code) out.add(code.toLowerCase());
    }
    return out;
  }

  private extractText(cc: any): string {
    const text = (cc as any)?.text;
    return typeof text === 'string' ? text.trim().toLowerCase() : '';
  }
}
